import { Alert, Button, Input, Select } from '@piggytechng/component-library';
import './WalletAction.scss';
import { useEffect, useMemo, useState } from 'react';
import { OverideButtonStyle, OverideInputStyle, OverideSelectStyle, formatToNaira } from '../../../../helpers/utils';
import useCustomers from '../../../../hooks/useCustomers';
import useTargetSavings, { TARGET_ADJUST_MAX_AMOUNT } from '../../../../hooks/useTargetSavings';
import ModalExecuteAction from '../../../../components/Modals/ModalExecuteAction';
import useRolePermissions from '../../../../hooks/useRolePermissions';
import { AdjustableTarget } from '../../../../types';
import WithdrawForUser from './WithdrawForUser';

const WalletActionTab = ({ userId }: any) => {
  const { isRunningWalletAction, handleWalletTransfer } = useCustomers();
  const { isSuperAdmin } = useRolePermissions({});
  const { fetchAdjustableTargets, isFetchingAdjustableTargets, adjustTargetBalance, isAdjustingTarget } =
    useTargetSavings();
  const [wallets] = useState([
    { value: 'piggybank', label: 'Piggybank' },
    { value: 'flexnaira', label: 'Flexnaira' },
    { value: 'housemoney', label: 'HouseMoney' },
    { value: 'target', label: 'Target' },
  ]);
  const [targets, setTargets] = useState<AdjustableTarget[]>([]);
  const [targetId, setTargetId] = useState('');

  const memoTemplates = {
    flexnaira: [
      {
        value: 'reversal',
        label: 'Rectified: Transaction Reversal. Transaction ID: ',
      },
      {
        value: 'credit',
        label: 'Flex Wallet Credited By Admin. Transaction ID: ',
      },
      {
        value: 'debit',
        label: 'Flex Wallet Debited By Admin. Transaction ID: ',
      },
    ],
    piggybank: [
      {
        value: 'dailysaving',
        label: 'Rectified: Daily Saving Deposit. Payment ID: ',
      },
      {
        value: 'weeklysaving',
        label: 'Rectified: Weekly Saving Deposit. Payment ID: ',
      },
      {
        value: 'monthlysaving',
        label: 'Rectified: Monthly Saving Deposit. Payment ID: ',
      },
      {
        value: 'safelockreverse',
        label: 'Rectified: Safelock Reversal. ',
      },
      {
        value: 'quicksave',
        label: 'Rectified: Quicksave deposit. Payment ID: ',
      },
      {
        value: 'reversal',
        label: 'Rectified: Transaction Reversal. Transaction ID: ',
      },
      {
        value: 'credit',
        label: 'Account Credited By Admin. Transaction ID: ',
      },
      {
        value: 'debit',
        label: 'Account Debited By Admin. Transaction ID: ',
      },
      {
        value: 'double_withdrawal',
        label: 'Double-Withdrawal To Bank. Transfer ID: ',
      },
    ],
    housemoney: [
      {
        value: 'reversal',
        label: 'Rectified: Transaction Reversal. Transaction ID: ',
      },
      {
        value: 'credit',
        label: 'HouseMoney Wallet Credited By Admin. Transaction ID: ',
      },
      {
        value: 'debit',
        label: 'HouseMoney Wallet Debited By Admin. Transaction ID: ',
      },
    ],
    target: [
      {
        value: 'reversal',
        label: 'Rectified: Transaction Reversal. Transaction ID: ',
      },
      {
        value: 'credit',
        label: 'Target Credited By Admin. Transaction ID: ',
      },
      {
        value: 'debit',
        label: 'Target Debited By Admin. Transaction ID: ',
      },
    ],
  };

  const [formData, setFormData] = useState({
    amount: '',
    type: '',
    wallet: '',
    memoTemplate: '',
    transactionId: '',
  });

  const resetForm = () => {
    setFormData({
      amount: '',
      type: '',
      wallet: '',
      memoTemplate: '',
      transactionId: '',
    });
    setTargetId('');
  };

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'transfer' | 'withdrawal'>('transfer');

  const style = { ...OverideSelectStyle, style: { ...OverideSelectStyle.style, zIndex: undefined } };

  const isTarget = formData.wallet === 'target';
  const isBusy = isRunningWalletAction || isAdjustingTarget;

  // Load the customer's active targets the first time Target is picked
  useEffect(() => {
    if (!isTarget || !userId) return;
    let cancelled = false;
    fetchAdjustableTargets(userId).then((list) => {
      if (!cancelled) setTargets(list);
    });
    return () => {
      cancelled = true;
    };
  }, [isTarget, userId]);

  const selectedTarget = useMemo(() => targets.find((t) => t.targetId === targetId), [targets, targetId]);

  const formatTargetBalance = (balance: AdjustableTarget['userBalance']) => {
    if (balance === null || balance === undefined || balance === '') return '';
    return typeof balance === 'number' ? formatToNaira(balance) : String(balance);
  };

  const targetOptions = targets.map((t) => {
    const balance = formatTargetBalance(t.userBalance);
    return { value: t.targetId, label: balance ? `${t.title} (${balance})` : t.title };
  });

  const selectedMemoTemplate = (
    memoTemplates.target.find((m) => m.value === formData.memoTemplate)?.label ?? ''
  ).trim();
  // The template label plus the transaction id typed in the form; the id is optional for a target adjustment
  const targetMemo = formData.transactionId.trim()
    ? `${selectedMemoTemplate} ${formData.transactionId.trim()}`
    : selectedMemoTemplate.replace(/Transaction ID:$/i, '').trim();

  const handleSubmit = (e: { preventDefault: () => void; target: any }) => {
    e.preventDefault();

    if (isTarget) {
      if (!isSuperAdmin) {
        Alert.warn('You do not have access to perform this action.');
        return;
      }
      if (!formData.amount || !formData.type || !formData.memoTemplate || !targetId) {
        Alert.error('Please pick a target, a transaction type, an amount and a memo');
        return;
      }
      const amount = Number(rawAmount);
      if (!amount || amount <= 0) {
        Alert.error('Enter an amount greater than zero');
        return;
      }
      if (amount > TARGET_ADJUST_MAX_AMOUNT) {
        Alert.error(`A single target adjustment cannot exceed ${formatToNaira(TARGET_ADJUST_MAX_AMOUNT)}`);
        return;
      }
      if (
        formData.type === 'debit' &&
        typeof selectedTarget?.userBalance === 'number' &&
        amount > selectedTarget.userBalance
      ) {
        Alert.error('Debit amount is more than the target balance');
        return;
      }
      const transactionId = formData.transactionId.trim();
      if (transactionId && !/^[A-Za-z0-9_-]{1,60}$/.test(transactionId)) {
        Alert.error('Transaction ID can only contain letters, numbers, _ or - (max 60 characters)');
        return;
      }
      if (targetMemo.length > 200) {
        Alert.error('Memo cannot be longer than 200 characters');
        return;
      }
      setShowAuthModal(true);
      return;
    }

    if (!formData.amount || !formData.type || !formData.wallet || !formData.memoTemplate || !formData.transactionId) {
      Alert.error('Please fill all fields');
      return;
    }

    setShowAuthModal(true);
  };

  const executeWalletTransfer = (password?: string) => {
    if (isTarget) {
      adjustTargetBalance(
        userId,
        targetId,
        {
          type: formData.type as 'credit' | 'debit',
          amount: Number(rawAmount),
          memo: targetMemo,
          ...(formData.transactionId.trim() ? { transactionId: formData.transactionId.trim() } : {}),
        },
        password ?? '',
        () => {
          setShowAuthModal(false);
          resetForm();
        },
      );
      return;
    }

    handleWalletTransfer(userId, { ...formData, amount: Number(rawAmount) }, password ?? '', () => {
      setShowAuthModal(false);
      resetForm();
    });
  };

  const rawAmount = useMemo(() => {
    // remove naira symbol and commas
    const res = Number(formData.amount.replace(/,/g, '').replace('₦', '')).toFixed(2);

    return res;
  }, [formData.amount]);

  return (
    <div className="wallets-details wallet-action-tab">
      <div className="history-tab-switch piggybank">
        <span
          className={`history-pill${activeTab === 'transfer' ? ' active' : ''}`}
          onClick={() => setActiveTab('transfer')}
        >
          Debit and Credit
        </span>
        <span
          className={`history-pill${activeTab === 'withdrawal' ? ' active' : ''}`}
          onClick={() => setActiveTab('withdrawal')}
        >
          Withdrawals
        </span>
      </div>

      {activeTab === 'withdrawal' ? (
        <WithdrawForUser userId={userId} />
      ) : (
        <form className="wallet-action" onSubmit={handleSubmit}>
          <Select
            label="Wallet Type"
            {...style}
            labelStyle={{ color: '#666766' }}
            backgroundColor="#F9FAFB"
            outlineColor="none"
            height="60px"
            placeholder="select wallet"
            value={formData.wallet}
            options={wallets}
            onChange={(e: any) => {
              setFormData({ ...formData, wallet: e, memoTemplate: '' });
              setTargetId('');
            }}
          />

          {isTarget && (
            <Select
              label="Target"
              {...style}
              labelStyle={{ color: '#666766' }}
              backgroundColor="#F9FAFB"
              outlineColor="none"
              height="60px"
              placeholder={
                isFetchingAdjustableTargets
                  ? 'Loading active targets...'
                  : targets.length
                    ? 'Select target'
                    : 'No active targets for this customer'
              }
              value={targetId}
              key={`target-${targets.length}`}
              onChange={(e: any) => setTargetId(e ?? '')}
              options={targetOptions}
            />
          )}

          <Select
            label="Transaction Type"
            {...style}
            labelStyle={{ color: '#666766' }}
            backgroundColor="#F9FAFB"
            outlineColor="none"
            height="60px"
            placeholder="Choose Transaction Type"
            value={formData.type}
            onChange={(e: any) => setFormData({ ...formData, type: e })}
            options={[
              {
                value: 'debit',
                label: 'Debit',
              },
              {
                value: 'credit',
                label: 'Credit',
              },
            ]}
          />

          <Input
            {...OverideInputStyle}
            label="Amount"
            className="user-form-input"
            height="60px"
            placeholder="Enter amount"
            backgroundColor="#F9FAFB"
            outlineColor="transparent"
            value={formData.amount}
            type="number"
            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
          />

          {isTarget && (
            <p className="wallet-action__helper">
              Max {formatToNaira(TARGET_ADJUST_MAX_AMOUNT)} per target adjustment. Transaction ID is optional for a
              target (letters, numbers, _ or -, max 60); when given it is appended to the memo.
            </p>
          )}

          <Input
            {...OverideInputStyle}
            label="Transaction ID"
            className="user-form-input"
            height="60px"
            placeholder="Enter transaction ID"
            backgroundColor="#F9FAFB"
            outlineColor="transparent"
            onChange={(e) => setFormData({ ...formData, transactionId: e.target.value })}
            value={formData.transactionId}
          />

          <Select
            label="Memo template"
            {...style}
            labelStyle={{ color: '#666766' }}
            backgroundColor="#F9FAFB"
            outlineColor="none"
            height="60px"
            placeholder="Choose Memo"
            value={formData.memoTemplate}
            onChange={(e: any) => setFormData({ ...formData, memoTemplate: e })}
            options={formData.wallet ? memoTemplates[formData.wallet as keyof typeof memoTemplates] : []}
          />

          <div></div>

          <div>
            <Button
              type="submit"
              loading={isBusy}
              {...OverideButtonStyle}
              style={{
                fontFamily: 'Jakarta',
                width: '100%',
              }}
            >
              Execute
            </Button>
          </div>

          <ModalExecuteAction
            modalIsOpen={showAuthModal}
            isLoading={isBusy}
            setIsOpen={setShowAuthModal}
            text={
              isTarget
                ? `${formData.type} ${selectedTarget?.title ?? 'this target'} with ${formatToNaira(Number(rawAmount) || 0)}?`
                : 'execute this wallet transfer?'
            }
            action={executeWalletTransfer}
            actionColor="#1734CA"
            buttonText="Execute"
          />
        </form>
      )}
    </div>
  );
};
export default WalletActionTab;
